<script setup lang="ts">
import { FaqProps } from "~/components/Contact/Faq.props"
import { CustomerServiceProps } from "~/components/Services/CustomerService.props"
import { SeoBaseProps } from "~/components/seo/Base.props"
import { customPaths } from "~/utils/constants"
import { buildScopedGenericPageCss } from "~/utils/genericPageCss"
import { checkPage } from "~/utils/pager"

const route = useRoute()
const { locale, t } = useI18n()

await checkPage(route)

const dayjs = useDayjs()

const { data: pageData, error } = await usePage(
  `/api/gluon/fetchGenericPage/${locale.value}/${
    route.params.generic as string
  }/generic.js`
)

if (error.value) {
  throw createError({
    message: error.value.message,
    statusCode: error.value.statusCode ?? 404
  })
}

const staticCustomerService = computed(
  (): CustomerServiceProps => ({
    cards: [
      {
        isIconBig: true,
        title: t("servicePage.customerService.card1.title"),
        text: t("servicePage.customerService.card1.text"),
        contact: {
          label: t("servicePage.customerService.card1.contact"),
          link: customPaths.faqs
        },
        iconType: "FAQ"
      },
      {
        isIconBig: true,
        title: t("servicePage.customerService.card2.title"),
        text: t("servicePage.customerService.card2.text"),
        contact: {
          label: t("servicePage.customerService.card2.contact"),
          link: customPaths.contactUs
        },
        iconType: "CustomerServiceMail"
      }
    ]
  })
)

const { data: questions } = await useFetch("/api/zendesk/getArticleListByIds", {
  method: "POST",
  body: {
    articleIDs: pageData.value?.faqs.questionIDs
  }
})

const faq = computed((): FaqProps | undefined => {
  if (!questions.value || !questions.value.length) return undefined

  return {
    label: pageData.value?.faqs.label ?? "",
    questions: questions.value?.map((q) => ({
      text: q.title,
      path: `${customPaths.faqs}#question-${q.id}`
    }))
  }
})

const seoData = computed((): SeoBaseProps => {
  return {
    ...pageData.value?.seo,
    metaTitle:
      `${pageData.value?.seo.metaTitle} - Pagina ${route.params.page}` ??
      undefined,
    metaDescription:
      `${pageData.value?.seo.metaDescription} - Pagina ${route.params.page}` ??
      undefined
  }
})

const genericPageId = computed(() => pageData.value?.Id ?? "default")

const genericPageScopeClass = computed(
  () => `cms-generic-page--${genericPageId.value}`
)

const genericPageScopedCss = computed(() =>
  buildScopedGenericPageCss(
    pageData.value?.styleCss?.styleCss,
    `.cms-generic-page.${genericPageScopeClass.value}`
  )
)

useHead(() => ({
  style: genericPageScopedCss.value
    ? [
        {
          key: `generic-page-css-${genericPageId.value}`,
          children: genericPageScopedCss.value
        }
      ]
    : []
}))
</script>

<template>
  <div :class="['cms-generic-page', genericPageScopeClass]">
    <SeoBase v-if="pageData?.seo" v-bind="seoData" />
    <div v-if="pageData">
      <div class="grid-standard">
        <div
          v-if="pageData.heroImg"
          class="
            col-span-full
            hidden
            overflow-hidden
            md:relative
            md:flex
            md:aspect-[4/1]
          "
        >
          <UtilsWrapperImage
            v-bind="pageData.heroImg"
            :preload="true"
            :img-attrs="{ fetchpriority: 'high' }"
            class="
              hero-image__desktop
              absolute
              left-0
              top-1/2
              w-full
              -translate-y-1/2
            "
          />
        </div>

        <div
          v-if="pageData.heroImgMobile"
          class="cover-image__mobile negative-margined col-span-full md:hidden"
        >
          <UtilsWrapperImage
            v-bind="pageData.heroImgMobile"
            :preload="true"
            :img-attrs="{ fetchpriority: 'high' }"
            class="
              wrapper-image-fixed-240 wrapper-image-full-container-centered
            "
          />
        </div>
      </div>
      <div v-if="pageData.contentItems?.length > 0">
        <div class="grid-standard">
          <div
            class="col-span-full pt-4 md:pt-6"
            v-if="pageData.title || pageData.description"
          >
            <h1 v-if="pageData.title" class="hyppo-bold md:mammoth-bold mb-2">
              {{ pageData.title }}
            </h1>
            <p v-if="pageData.description" class="beaver md:pig">
              {{ pageData.description }}
            </p>
          </div>
        </div>
        <div>
          <div class="col-span-full">
            <DynamicContent
              v-if="pageData?.contentItems"
              class="cms-content cms-content__generic-page"
              :components="pageData?.contentItems"
            />
          </div>
        </div>
      </div>
      <ServicesPage
        v-else
        v-bind="{
          ...pageData,
          customerService: staticCustomerService,
          faq: faq
        }"
      />
      <TextBlocksCategory
        v-if="pageData.footerText"
        :title="pageData.footerTitle"
        :description="pageData.footerText"
      />
    </div>
  </div>
</template>
<style lang="scss" scoped>
.cms-content {
  :deep(.cms-component-carousel),
  :deep(.cms-component-grid),
  :deep(.cms-popular-brand),
  :deep(.cms-component-algolia-rails-products) {
    @apply col-span-full;
  }
}
</style>
